import { idadeEmMeses } from './derivacoes.js';

/**
 * Classe do animal no portal do haras. Decisão de 27/09/2026, registrada em
 * docs/origem/audios-rafael-2026-09-27.md.
 *
 * Não confundir com `faixaEtaria`, que agrupa contemporâneos para análise: lá
 * "potro" é menos de 12 meses; aqui é menos de 36 meses e sem filho.
 *
 * A classe é sempre derivada, nunca gravada: depende da data de referência, e a
 * mesma ficha antiga tem que continuar mostrando o potro que o animal era.
 */
export type Classe = 'potro' | 'garanhao' | 'egua' | 'castrado';

export type FaixaDoPotro = '0-12' | '12-24' | '24-36';

export interface SujeitoDaClasse {
  sex: 'macho_inteiro' | 'castrado' | 'femea';
  birthDate: string;
  /** Datas de nascimento dos filhos cadastrados (pai ou mãe apontando para ele). */
  nascimentosDosFilhos: readonly string[];
}

const LIMITE_POTRO_MESES = 36;

const CLASSE_DO_SEXO: Record<SujeitoDaClasse['sex'], Exclude<Classe, 'potro'>> = {
  macho_inteiro: 'garanhao',
  femea: 'egua',
  castrado: 'castrado',
};

/**
 * Potro cumpre as duas regras ao mesmo tempo: menos de 3 anos e nenhum filho
 * nascido até `naData`. Descumpriu uma, vai para a classe do sexo.
 *
 * Limite conhecido: só conta filho cadastrado. Um filho fora da base não tira
 * o animal da classe de potro.
 */
export function classeDoAnimal(s: SujeitoDaClasse, naData: string): Classe {
  const jovem = idadeEmMeses(s.birthDate, naData) < LIMITE_POTRO_MESES;
  // Datas ISO comparam como texto.
  const temFilho = s.nascimentosDosFilhos.some((d) => d <= naData);
  return jovem && !temFilho ? 'potro' : CLASSE_DO_SEXO[s.sex];
}

/** Faixa do potro, para filtro. Inclui o início e exclui o fim de cada faixa. */
export function faixaDoPotro(meses: number): FaixaDoPotro | null {
  if (meses < 0 || meses >= LIMITE_POTRO_MESES) return null;
  if (meses < 12) return '0-12';
  if (meses < 24) return '12-24';
  return '24-36';
}
